import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save,
  X,
  Plus,
  ChevronDown,
  Search,
  FileText,
  Clock,
  Target,
  AlertTriangle
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const isValidMongoId = (id) =>
  typeof id === 'string' && /^[a-f\d]{24}$/i.test(id);

const EditMilestone = () => {
  const navigate = useNavigate();
  const { id, projectId, milestoneId } = useParams();
  const effectiveMilestoneId = milestoneId || id;
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    projectId: '',
    status: 'pending',
    priority: 'medium',
    startDate: '',
    dueDate: '',
    assignedTo: '',
    deliverables: [],
    dependencies: [],
    risks: [],
    estimatedHours: '',
    actualHours: '',
    notes: ''
  });

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [internalMilestoneId, setInternalMilestoneId] = useState(null);
  
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef(null);

  useEffect(() => {
    if (effectiveMilestoneId) {
      loadMilestoneData();
      loadProjects();
      loadUsers();
    }
  }, [effectiveMilestoneId]);

  const loadMilestoneData = async () => {
    if (!effectiveMilestoneId) return;
    
    setLoadingData(true);
    try {
      if (projectId) {
        try {
          const projectResponse = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}`, {
            headers: API_CONFIG.HEADERS
          });
          
          if (projectResponse.ok) {
            const projectData = await projectResponse.json();
            const project = projectData.data?.project || projectData.project;
            if (project && project.milestones) {
              const milestone = project.milestones.find(m => 
                (m._id && m._id.toString() === effectiveMilestoneId) || 
                (m.id && m.id.toString() === effectiveMilestoneId)
              );
              
              if (milestone) {
                setInternalMilestoneId(milestone._id || milestone.id);
                const formatDate = (dateString) => {
                  if (!dateString) return '';
                  try {
                    const date = new Date(dateString);
                    return date.toISOString().split('T')[0];
                  } catch {
                    return '';
                  }
                };
                
                const assignedToValue =
                  milestone.assignedTo?._id ||
                  milestone.assignedTo?.id ||
                  (isValidMongoId(milestone.assignedTo) ? milestone.assignedTo : '');

                setFormData({
                  name: milestone.name || '',
                  description: milestone.description || '',
                  projectId: projectId,
                  status: (milestone.status?.toLowerCase() === 'not started' ? 'pending' : milestone.status?.toLowerCase()) || 'pending',
                  priority: milestone.priority?.toLowerCase() || 'medium',
                  startDate: formatDate(milestone.startDate),
                  dueDate: formatDate(milestone.dueDate),
                  assignedTo: assignedToValue,
                  deliverables: milestone.deliverables || [],
                  dependencies: milestone.dependencies || [],
                  risks: milestone.risks || [],
                  estimatedHours: milestone.estimatedHours?.toString() || '',
                  actualHours: milestone.actualHours?.toString() || '',
                  notes: milestone.notes || ''
                });
                setLoadingData(false);
                return;
              }
            }
          }
        } catch (err) {
          console.error('Error loading milestone from project:', err);
        }
      }
      
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/milestones`, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.milestones) {
          const milestone = data.milestones.find(m => 
            (m._id && m._id.toString() === effectiveMilestoneId) || 
            (m.id && m.id.toString() === effectiveMilestoneId)
          );
          
          if (milestone) {
            setInternalMilestoneId(milestone._id || milestone.id);
            
            const formatDate = (dateString) => {
              if (!dateString) return '';
              try {
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
              } catch {
                return '';
              }
            };
            
            setFormData({
              name: milestone.name || '',
              description: milestone.description || '',
              projectId: projectId || milestone.projectId || milestone.project?._id || milestone.project?.id || '',
              status: milestone.status?.toLowerCase() || 'pending',
              priority: milestone.priority?.toLowerCase() || 'medium',
              startDate: formatDate(milestone.startDate),
              dueDate: formatDate(milestone.dueDate),
              assignedTo: milestone.assignedTo?._id || milestone.assignedTo?.id || milestone.assignedTo || '',
              deliverables: milestone.deliverables || [],
              dependencies: milestone.dependencies || [],
              risks: milestone.risks || [],
              estimatedHours: milestone.estimatedHours?.toString() || '',
              actualHours: milestone.actualHours?.toString() || '',
              notes: milestone.notes || ''
            });
          } else {
            showError('Milestone not found');
            navigate(projectId ? `/projects/view/${projectId}?tab=milestones` : '/milestones');
          }
        }
      } else {
        showError('Failed to load milestone data');
        navigate(projectId ? `/projects/view/${projectId}?tab=milestones` : '/milestones');
      }
    } catch (error) {
      console.error('Error loading milestone:', error);
      showError('Error loading milestone data');
      navigate(projectId ? `/projects/view/${projectId}?tab=milestones` : '/milestones');
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
    };

    if (isProjectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProjectDropdownOpen]);

  const filteredProjects = projects.filter(project => {
    const name = project.name || project.title || '';
    const code = project.projectCode || project.code || '';

    return (
      name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      code.toLowerCase().includes(projectSearchTerm.toLowerCase())
    );
  });

  const selectedProject = projects.find(p => String(p._id || p.id) === String(formData.projectId));

  const loadProjects = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) throw new Error('Failed to load projects');

      const result = await response.json();

      const projectsList =
        Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.projects)
          ? result.data.projects
          : Array.isArray(result?.projects)
          ? result.projects
          : [];

      setProjects(projectsList);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.USERS, {
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) throw new Error('Failed to load users');

      const result = await response.json();

      const usersList =
        Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.users)
          ? result.users
          : [];

      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleAddArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) {
      console.warn('Form submission already in progress, ignoring duplicate submit');
      return;
    }
    
    console.log('Form submitted', formData);
    setLoading(true);

    if (!formData.projectId) {
      console.log('Validation failed: No project selected');
      showError('Please select a project');
      setLoading(false);
      return;
    }

    if (!formData.name) {
      console.log('Validation failed: No milestone name');
      showError('Please enter a milestone name');
      setLoading(false);
      return;
    }

    try {
      const effectiveProjectId = projectId || formData.projectId;
      const effectiveMilestoneIdForUpdate = effectiveMilestoneId || internalMilestoneId;
      
      if (!effectiveProjectId || !effectiveMilestoneIdForUpdate) {
        showError('Project ID and Milestone ID are required');
        setLoading(false);
        return;
      }

      const { projectId: _, ...updateData } = formData;

      let safeStatus = updateData.status;
      if (safeStatus === 'not started') safeStatus = 'pending';

      // Ensure priority is valid
      let safePriority = updateData.priority;
      // If backend expects Title Case (like in your CreateProject issue), capitalize it:
      // safePriority = safePriority.charAt(0).toUpperCase() + safePriority.slice(1); 
      // But based on your Edit code dropdowns, it looks like it wants lowercase. 
      // We will stick to lowercase unless you see a priority error.
      // --- FIX END ---
      
      const requestBody = {
        ...updateData,
        status: safeStatus, 
        startDate: updateData.startDate ? new Date(updateData.startDate) : null,
        dueDate: updateData.dueDate ? new Date(updateData.dueDate) : null,
        estimatedHours: updateData.estimatedHours ? parseFloat(updateData.estimatedHours) : null,
        actualHours: updateData.actualHours ? parseFloat(updateData.actualHours) : null
      };
      
      if (!isValidMongoId(requestBody.assignedTo)) {
        delete requestBody.assignedTo;
      }
      
      console.log('Sending PUT request to:', `${API_CONFIG.ENDPOINTS.PROJECTS}/${effectiveProjectId}/milestones/${effectiveMilestoneIdForUpdate}`);
      console.log('Request body:', requestBody);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/${effectiveProjectId}/milestones/${effectiveMilestoneIdForUpdate}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Success response:', responseData);
        success('Milestone updated successfully!');
        navigate('/projects/milestone-tracking');
      } else {
        let errorMessage = 'Failed to update milestone';
        try {
          const errorData = await response.json();
          console.log('Error response:', errorData);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.log('Failed to parse error response:', parseError);
          errorMessage = response.statusText || `Server returned ${response.status} error`;
        }
        console.log('Showing error:', errorMessage);
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Error updating milestone:', error);
      showError(error.message || 'Error updating milestone. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Edit Milestone"
          description="Update milestone information"
        />
        <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  const tabs = [
    {
      id: 'basic',
      label: 'Milestone Setup',
      icon: <FileText size={18} />
    },
    {
      id: 'timeline',
      label: 'Timeline & Resources',
      icon: <Clock size={18} />
    },
    {
      id: 'deliverables',
      label: 'Deliverables & Dependencies',
      icon: <Target size={18} />
    },
    {
      id: 'risks',
      label: 'Risks & Tracking',
      icon: <AlertTriangle size={18} />
    }
  ];

  const renderBasicInformation = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Basic Information</h3>
        <p className="section-description">Milestone name, description, and basic details</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Project *</label>
        <div ref={projectDropdownRef} style={{ position: 'relative' }}>
          <div
            className="form-control"
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px 12px',
              minHeight: '38px'
            }}
            onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
          >
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedProject ? (
                <span style={{ color: '#1f2937' }}>
                  {selectedProject.name} {selectedProject.projectCode ? `(${selectedProject.projectCode})` : ''}
                </span>
              ) : formData.projectId ? (
                <span style={{ color: '#9ca3af' }}>Project ID: {formData.projectId} (not found)</span>
              ) : (
                <span style={{ color: '#9ca3af' }}>Select Project</span>
              )}
            </div>
            <ChevronDown 
              size={18} 
              style={{ 
                color: '#6b7280',
                transform: isProjectDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </div>
          
          {isProjectDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              zIndex: 1000,
              marginTop: '4px',
              maxHeight: '300px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{ 
                padding: '8px',
                borderBottom: '1px solid #e5e7eb',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white'
              }}>
                <div style={{ position: 'relative' }}>
                  <Search 
                    size={16} 
                    style={{ 
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af'
                    }} 
                  />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search projects..."
                    value={projectSearchTerm}
                    onChange={(e) => {
                      setProjectSearchTerm(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      paddingLeft: '36px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                    autoFocus
                  />
                </div>
              </div>
              
              <div style={{
                overflowY: 'auto',
                maxHeight: '250px'
              }}>
                {filteredProjects.length > 0 ? (
                  filteredProjects.map(project => {
                    const projectId = project.id || project._id;
                    return (
                      <div
                        key={projectId}
                        onClick={() => {
                          console.log('Project selected:', project, 'ID:', projectId);
                          setFormData(prev => {
                            const updated = { ...prev, projectId: projectId };
                            console.log('Updated formData:', updated);
                            return updated;
                          });
                          setIsProjectDropdownOpen(false);
                          setProjectSearchTerm('');
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          backgroundColor: formData.projectId === projectId ? '#eff6ff' : 'white',
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background-color 0.15s',
                          fontFamily: 'var(--font-family, Inter, sans-serif)'
                        }}
                        onMouseEnter={(e) => {
                          if (formData.projectId !== projectId) {
                            e.target.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (formData.projectId !== projectId) {
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: formData.projectId === projectId ? '600' : '400', color: '#1f2937' }}>
                          {project.name}
                        </div>
                        {project.projectCode && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {project.projectCode}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    No projects found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {!formData.projectId && (
          <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
            Project is required
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Milestone Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="form-control"
          placeholder="Enter milestone name"
          required
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          className="form-control"
          placeholder="Describe the milestone objectives and scope"
          rows={4}
        />
      </div>

      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="form-select"
          >
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="on-hold">On Hold</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            className="form-select"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderTimelineResources = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Timeline</h3>
        <p className="section-description">Set milestone start and due dates</p>
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Due Date *</label>
          <input
            type="date"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Assignment</h3>
        <p className="section-description">Assign milestone to team members</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Assigned To</label>
        <select
          name="assignedTo"
          value={formData.assignedTo}
          onChange={handleInputChange}
          className="form-select"
        >
          <option value="">Select Assignee</option>
          {users.map(user => {
            const userId = user._id || user.id;
            const userName =
              `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
              user.email ||
              'Unnamed User';

            return (
              <option key={userId} value={userId}>
                {userName}
              </option>
            );
          })}
        </select>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Time Tracking</h3>
        <p className="section-description">Track estimated and actual hours for this milestone</p>
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Estimated Hours</label>
          <input
            type="number"
            name="estimatedHours"
            value={formData.estimatedHours}
            onChange={handleInputChange}
            className="form-control"
            placeholder="0"
            min="0"
            step="0.5"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Actual Hours</label>
          <input
            type="number"
            name="actualHours"
            value={formData.actualHours}
            onChange={handleInputChange}
            className="form-control"
            placeholder="0"
            min="0"
            step="0.5"
          />
        </div>
      </div>
    </div>
  );

  const renderDeliverablesDependencies = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Deliverables</h3>
        <p className="section-description">Define key deliverables for this milestone</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Deliverables</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add deliverable"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const value = e.target.value.trim();
                  if (!value) return;

                  setFormData(prev => ({
                    ...prev,
                    deliverables: [...prev.deliverables, value]
                  }));

                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  const value = input.value.trim();
                  if (!value) return;

                  setFormData(prev => ({
                    ...prev,
                    deliverables: [...prev.deliverables, value]
                  }));

                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.deliverables.map((deliverable, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={deliverable}
                  onChange={(e) => handleArrayChange('deliverables', index, e.target.value)}
                  className="form-control"
                  placeholder="Enter deliverable"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem('deliverables', index)}
                  className="btn btn-sm btn-outline-danger"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Dependencies</h3>
        <p className="section-description">List any dependencies for this milestone</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Dependencies</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add dependency"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const value = e.target.value.trim();
                  if (!value) return;

                  setFormData(prev => ({
                    ...prev,
                    dependencies: [...prev.dependencies, value]
                  }));

                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  const value = input.value.trim();
                  if (!value) return;

                  setFormData(prev => ({
                    ...prev,
                    dependencies: [...prev.dependencies, value]
                  }));

                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.dependencies.map((dependency, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={dependency}
                  onChange={(e) => handleArrayChange('dependencies', index, e.target.value)}
                  className="form-control"
                  placeholder="Enter dependency"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem('dependencies', index)}
                  className="btn btn-sm btn-outline-danger"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRisksTracking = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Risks</h3>
        <p className="section-description">Identify potential risks for this milestone</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Risks</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add risk"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const value = e.target.value.trim();
                  if (!value) return;

                  setFormData(prev => ({
                    ...prev,
                    risks: [...prev.risks, value]
                  }));

                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  const value = input.value.trim();
                  if (!value) return;

                  setFormData(prev => ({
                    ...prev,
                    risks: [...prev.risks, value]
                  }));

                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.risks.map((risk, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={risk}
                  onChange={(e) => handleArrayChange('risks', index, e.target.value)}
                  className="form-control"
                  placeholder="Enter risk"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem('risks', index)}
                  className="btn btn-sm btn-outline-danger"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Additional Notes</h3>
        <p className="section-description">Add any additional notes or comments</p>
      </div>
      
      <div className="form-group">
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          className="form-control"
          placeholder="Add any additional notes or comments"
          rows={6}
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInformation();
      case 'timeline':
        return renderTimelineResources();
      case 'deliverables':
        return renderDeliverablesDependencies();
      case 'risks':
        return renderRisksTracking();
      default:
        return renderBasicInformation();
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Edit Milestone"
        description="Update milestone information and details"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
          <form onSubmit={handleSubmit} noValidate style={{ width: '100%' }}>
            {/* Tab Navigation */}
            <div className="project-tabs">
              <div className="project-tabs-nav">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <div className="project-tab-icon">
                      {tab.icon}
                    </div>
                    <div className="project-tab-content">
                      <div className="project-tab-label">{tab.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="project-form-card">
              {renderTabContent()}
            </div>

            {/* Form Actions */}
            <div className="project-form-actions">
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/projects/milestone-tracking')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Updating Milestone...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Update Milestone
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default EditMilestone;